import { useCallback, useEffect, useRef, useState } from "react";
import type { Redaction, RedactionType } from "../../shared/types";
import { clamp, round, MIN_REDACTION_PCT } from "../../shared/redaction";
import { newId } from "../api";

type Drag =
  | { kind: "draw"; x0: number; y0: number; x: number; y: number }
  | { kind: "move"; id: string; dx: number; dy: number }
  | { kind: "resize"; id: string };

const newRedaction = (x: number, y: number, w: number, h: number, type: RedactionType = "blur"): Redaction => ({
  id: newId(),
  type,
  x: round(x),
  y: round(y),
  w: round(w),
  h: round(h),
});

export function useRedactionCanvas(initial: Redaction[], opts: { onDirty?: (d: boolean) => void } = {}) {
  const [redactions, setRedactions] = useState<Redaction[]>(initial);
  const [past, setPast] = useState<Redaction[][]>([]);
  const [future, setFuture] = useState<Redaction[][]>([]);
  const [sel, setSel] = useState<string | null>(null);
  const [drag, setDrag] = useState<Drag | null>(null);
  const canvasRef = useRef<HTMLDivElement>(null);
  const onDirty = useRef(opts.onDirty);
  onDirty.current = opts.onDirty;

  const [cleanKey, setCleanKey] = useState<string>(() => JSON.stringify(initial));
  const dirty = JSON.stringify(redactions) !== cleanKey;
  useEffect(() => { onDirty.current?.(dirty); }, [dirty]);

  const markClean = useCallback((current?: Redaction[]) => {
    setCleanKey(JSON.stringify(current ?? redactions));
  }, [redactions]);

  const reset = useCallback((next: Redaction[]) => {
    setRedactions(next);
    setPast([]);
    setFuture([]);
    setSel(null);
    setDrag(null);
    setCleanKey(JSON.stringify(next));
  }, []);

  const commit = (next: Redaction[]) => {
    setPast((p) => [...p, redactions].slice(-100));
    setFuture([]);
    setRedactions(next);
  };

  const undo = () => {
    if (!past.length) return;
    setFuture((f) => [...f, redactions].slice(-100));
    setRedactions(past[past.length - 1]);
    setPast((p) => p.slice(0, -1));
    setSel(null);
  };

  const redo = () => {
    if (!future.length) return;
    setPast((p) => [...p, redactions].slice(-100));
    setRedactions(future[future.length - 1]);
    setFuture((f) => f.slice(0, -1));
    setSel(null);
  };

  const toPct = (e: { clientX: number; clientY: number }) => {
    const r = canvasRef.current!.getBoundingClientRect();
    return {
      x: clamp(((e.clientX - r.left) / r.width) * 100, 0, 100),
      y: clamp(((e.clientY - r.top) / r.height) * 100, 0, 100),
    };
  };

  const onPointerDown = (e: React.PointerEvent) => {
    if (e.button !== 0) return;
    const p = toPct(e);
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    setSel(null);
    setDrag({ kind: "draw", x0: p.x, y0: p.y, x: p.x, y: p.y });
  };

  const onBoxDown = (e: React.PointerEvent, r: Redaction, resize: boolean) => {
    e.stopPropagation();
    if (e.button !== 0) return;
    canvasRef.current!.setPointerCapture(e.pointerId);
    setSel(r.id);
    const p = toPct(e);
    setPast((ps) => [...ps, redactions].slice(-100));
    setFuture([]);
    setDrag(resize ? { kind: "resize", id: r.id } : { kind: "move", id: r.id, dx: p.x - r.x, dy: p.y - r.y });
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (!drag) return;
    const p = toPct(e);
    if (drag.kind === "draw") {
      setDrag({ ...drag, x: p.x, y: p.y });
    } else if (drag.kind === "move") {
      setRedactions(redactions.map((r) =>
        r.id === drag.id
          ? { ...r, x: round(clamp(p.x - drag.dx, 0, 100 - r.w)), y: round(clamp(p.y - drag.dy, 0, 100 - r.h)) }
          : r
      ));
    } else {
      setRedactions(redactions.map((r) =>
        r.id === drag.id
          ? { ...r, w: round(clamp(p.x - r.x, MIN_REDACTION_PCT, 100 - r.x)), h: round(clamp(p.y - r.y, MIN_REDACTION_PCT, 100 - r.y)) }
          : r
      ));
    }
  };

  const onPointerUp = (e: React.PointerEvent) => {
    if (!drag) return;
    try { (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId); } catch { /* ignore */ }
    if (drag.kind === "draw") {
      const x = round(Math.min(drag.x0, drag.x));
      const y = round(Math.min(drag.y0, drag.y));
      const w = round(Math.abs(drag.x - drag.x0));
      const h = round(Math.abs(drag.y - drag.y0));
      if (w >= MIN_REDACTION_PCT && h >= MIN_REDACTION_PCT) {
        const item = newRedaction(x, y, w, h, "blur");
        commit([...redactions, item]);
        setSel(item.id);
      }
    }
    setDrag(null);
  };

  const onPointerCancel = () => setDrag(null);

  const selected = redactions.find((r) => r.id === sel) ?? null;

  const patchSel = (patch: Partial<Redaction>) => {
    if (!sel) return;
    commit(redactions.map((r) => (r.id === sel ? { ...r, ...patch } : r)));
  };

  const removeSel = () => {
    if (!sel) return;
    commit(redactions.filter((r) => r.id !== sel));
    setSel(null);
  };

  const addCentered = (type: RedactionType = "blur") => {
    const item = newRedaction(35, 40, 30, 20, type);
    commit([...redactions, item]);
    setSel(item.id);
  };

  const draft = drag?.kind === "draw"
    ? { x: Math.min(drag.x0, drag.x), y: Math.min(drag.y0, drag.y), w: Math.abs(drag.x - drag.x0), h: Math.abs(drag.y - drag.y0) }
    : null;

  return {
    redactions,
    sel,
    setSel,
    selected,
    dirty,
    canUndo: past.length > 0,
    canRedo: future.length > 0,
    depth: past.length,
    undo,
    redo,
    reset,
    markClean,
    commit,
    patchSel,
    removeSel,
    addCentered,
    canvasRef,
    draft,
    onBoxDown,
    handlers: { onPointerDown, onPointerMove, onPointerUp, onPointerCancel },
  };
}

export type RedactionCanvas = ReturnType<typeof useRedactionCanvas>;

export function RedactionBoxes({ c }: { c: RedactionCanvas }) {
  return (
    <>
      {c.redactions.map((r, i) => (
        <div
          key={r.id}
          className={`redaction-box redaction-${r.type} ${r.id === c.sel ? "sel" : ""}`}
          style={{ left: `${r.x}%`, top: `${r.y}%`, width: `${r.w}%`, height: `${r.h}%` }}
          onPointerDown={(e) => c.onBoxDown(e, r, false)}
        >
          <span className="lbl">{i + 1} {r.type === "blackout" ? "Blackout" : "Blur"}</span>
          {r.id === c.sel && <span className="handle" onPointerDown={(e) => c.onBoxDown(e, r, true)} />}
        </div>
      ))}
      {c.draft && (
        <div
          className="redaction-draft"
          style={{ left: `${c.draft.x}%`, top: `${c.draft.y}%`, width: `${c.draft.w}%`, height: `${c.draft.h}%` }}
        />
      )}
    </>
  );
}
